import type { BlockStyleOverrides } from "@/components/website-builder/blocks/types"
import { ColumnGrid } from "@/components/website-builder/ColumnGrid"
import { getSectionVariant } from "./variants"
import { CardsFeatureList, CardsOverlap } from "./cards_variants"
import { resolveItemStyle, type BlockComponentProps, HEADING_COLOR } from "./shared"
import { sanitizeHtml, safeHref } from "@/lib/sanitizeHtml"

/** `grid` (default / unknown variant) is the original layout, unchanged for all existing sections. */
export function CardsBlock(props: BlockComponentProps) {
  switch (getSectionVariant(props.content)) {
    case "feature-list":
      return <CardsFeatureList {...props} />
    case "overlap":
      return <CardsOverlap {...props} />
    default:
      return <CardsGridDefault {...props} />
  }
}

function CardsGridDefault({ content, resolved }: BlockComponentProps) {
  const ch = (content?.heading as string) ?? ""
  const cSub = (content?.subheading as string) ?? ""
  const cItems = ((content?.items as Array<{
    title?: string
    body?: string
    image_url?: string
    cta_text?: string
    cta_url?: string
    visible?: boolean
  }>) ?? []).filter((item) => item.visible !== false)
  const cCols = Math.min(3, Math.max(2, (content?.columns as number) ?? 2))
  const primaryColor = "var(--nos-color-primary, #3b82f6)"
  if (cItems.length === 0) return null
  const cardsSectionClass = resolved.hasOverrides
    ? `px-4 @sm:px-6 @md:px-8 ${resolved.paddingClass || "py-8"} ${resolved.cornerClass} ${resolved.alignClass}`.trim()
    : "px-4 py-8 @sm:px-6 @md:px-8"
  return (
    <section className={cardsSectionClass} style={resolved.sectionStyle}>
      {ch && (
        <h2 className="text-2xl font-bold text-center" style={{ color: HEADING_COLOR }}>
          {ch}
        </h2>
      )}
      {cSub && <p className="text-center text-muted-foreground mt-1 mb-6">{cSub}</p>}
      {/* NOS-234: match the standard section content width (SectionContainer's
          max-w-6xl) so the card row visually spans the heading's width. */}
      <div className="max-w-6xl mx-auto">
        <ColumnGrid columns={cCols} itemCount={cItems.length}>
          {cItems.map((card, i) => {
            const cardItemStyle = resolveItemStyle((card as Record<string, unknown>)._styles as BlockStyleOverrides | undefined)
            const cardTextColor = cardItemStyle.color as string | undefined
            const cardCustomClass = ((card as Record<string, unknown>)._styles as BlockStyleOverrides | undefined)?.custom_class
            return (
            <div key={i} className={["rounded-xl border bg-card p-6", cardCustomClass].filter(Boolean).join(" ")} style={cardItemStyle}>
              {card.image_url && (
                <img
                  src={card.image_url}
                  alt={card.title ?? ""}
                  className="w-full h-32 object-cover rounded-lg mb-4"
                />
              )}
              {card.title && <h3 className="font-semibold text-lg">{card.title}</h3>}
              {card.body && (
                <p
                  className={`text-sm mt-2${cardTextColor ? "" : " text-muted-foreground"}`}
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(card.body) }}
                />
              )}
              {card.cta_text && card.cta_url && (
                <a
                  href={safeHref(card.cta_url)}
                  className="inline-block mt-4 text-sm font-medium"
                  style={{ color: cardTextColor ?? primaryColor }}
                >
                  {card.cta_text} →
                </a>
              )}
            </div>
            )
          })}
        </ColumnGrid>
      </div>
    </section>
  )
}
